import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link,
  Divider,
  InputAdornment,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Visibility, VisibilityOff, PersonAdd, Email, Lock, Person, MarkEmailRead } from '@mui/icons-material';
import GoogleSignInButton from '../components/auth/GoogleSignInButton';

// Dark theme colors matching marketing pages
const colors = {
  bgPrimary: '#0a0a0a',
  bgSecondary: '#141414',
  bgSurface: '#1a1a1a',
  border: '#2a2a2a',
  textPrimary: '#e5e5e5',
  textSecondary: '#a3a3a3',
  textMuted: '#737373',
  accentPrimary: '#3b82f6',
  accentSecondary: '#8b5cf6',
  success: '#22c55e',
  error: '#ef4444'
};

const PageWrapper = styled(Box)({
  minHeight: '100vh',
  backgroundColor: colors.bgPrimary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px'
});

const FormCard = styled(Box)({
  backgroundColor: colors.bgSurface,
  border: `1px solid ${colors.border}`,
  borderRadius: '16px',
  padding: '40px',
  maxWidth: '420px',
  width: '100%'
});

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: colors.bgSecondary,
    color: colors.textPrimary,
    borderRadius: '8px',
    '& fieldset': {
      borderColor: colors.border,
    },
    '&:hover fieldset': {
      borderColor: colors.accentPrimary,
    },
    '&.Mui-focused fieldset': {
      borderColor: colors.accentPrimary,
    },
  },
  '& .MuiInputLabel-root': {
    color: colors.textSecondary,
    '&.Mui-focused': {
      color: colors.accentPrimary,
    },
  },
  '& .MuiInputAdornment-root': {
    color: colors.textMuted,
  },
});

const GradientButton = styled(Button)({
  background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, ${colors.accentSecondary} 100%)`,
  color: 'white',
  fontWeight: 600,
  textTransform: 'none',
  padding: '12px 24px',
  borderRadius: '8px',
  fontSize: '1rem',
  '&:hover': {
    background: `linear-gradient(135deg, ${colors.accentPrimary} 20%, ${colors.accentSecondary} 100%)`,
    boxShadow: `0 4px 20px rgba(59, 130, 246, 0.4)`,
  },
  '&:disabled': {
    background: colors.border,
    color: colors.textMuted,
  },
});

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Set once an unverified signup succeeds, which swaps the form for a "check
  // your email" panel instead of navigating into the app.
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [resending, setResending] = useState(false);
  const { register, resendVerification } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect');

  const getRedirectPath = () => {
    if (redirectUrl && redirectUrl.startsWith('/') && !redirectUrl.startsWith('//')) {
      return redirectUrl;
    }
    return '/dashboard';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const result = await register(name, email, password);
      if (result?.requiresVerification) {
        // Unverified signup: no token was issued, so show the confirmation panel
        // rather than navigating into the app.
        setPendingVerificationEmail(result.email || email);
      } else {
        navigate(getRedirectPath());
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to register. Please try again.');
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setResendMessage('');
    setResending(true);
    try {
      await resendVerification(pendingVerificationEmail);
      setResendMessage('If your account still needs verifying, a new link is on its way.');
    } catch (err) {
      setResendMessage('Could not resend right now. Please try again shortly.');
    }
    setResending(false);
  };

  if (pendingVerificationEmail) {
    return (
      <PageWrapper>
        <Container maxWidth="sm">
          <FormCard>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '12px',
                  background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, ${colors.accentSecondary} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}
              >
                <MarkEmailRead sx={{ fontSize: 28, color: 'white' }} />
              </Box>
              <Typography variant="h4" sx={{ color: colors.textPrimary, fontWeight: 700, mb: 1 }}>
                Check your email
              </Typography>
              <Typography variant="body1" sx={{ color: colors.textSecondary }}>
                We sent a verification link to{' '}
                <Box component="span" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                  {pendingVerificationEmail}
                </Box>
                . Click it to activate your account, then sign in.
              </Typography>
            </Box>

            {resendMessage && (
              <Alert
                severity="info"
                sx={{
                  mb: 3,
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  color: colors.textPrimary,
                  border: `1px solid ${colors.accentPrimary}`,
                  '& .MuiAlert-icon': { color: colors.accentPrimary }
                }}
              >
                {resendMessage}
              </Alert>
            )}

            <GradientButton
              fullWidth
              onClick={handleResend}
              disabled={resending}
              sx={{ mb: 2 }}
            >
              {resending ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Resend verification email'}
            </GradientButton>

            <Box textAlign="center">
              <Link
                component={RouterLink}
                to={redirectUrl ? `/login?redirect=${encodeURIComponent(redirectUrl)}` : '/login'}
                sx={{
                  color: colors.accentPrimary,
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Back to Sign In
              </Link>
            </Box>
          </FormCard>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Container maxWidth="sm">
        <FormCard>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, ${colors.accentSecondary} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}
            >
              <PersonAdd sx={{ fontSize: 28, color: 'white' }} />
            </Box>
            <Typography
              variant="h4"
              sx={{
                color: colors.textPrimary,
                fontWeight: 700,
                mb: 1
              }}
            >
              Create Account
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: colors.textSecondary }}
            >
              Start your free trial today
            </Typography>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                color: colors.error,
                border: `1px solid ${colors.error}`,
                '& .MuiAlert-icon': { color: colors.error }
              }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <StyledTextField
              margin="normal"
              required
              fullWidth
              id="name"
              label="Full Name"
              name="name"
              autoComplete="name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person />
                  </InputAdornment>
                ),
              }}
            />
            <StyledTextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />
            <StyledTextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password (min. 6 characters)"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: colors.textMuted }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <StyledTextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      sx={{ color: colors.textMuted }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <GradientButton
              type="submit"
              fullWidth
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Create Account'}
            </GradientButton>

            <Divider sx={{ my: 2, borderColor: colors.border }}>
              <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                OR
              </Typography>
            </Divider>

            <Box sx={{ mb: 2 }}>
              <GoogleSignInButton redirectPath={getRedirectPath()} label="Sign up with Google" />
            </Box>

            <Box textAlign="center">
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                Already have an account?{' '}
                <Link
                  component={RouterLink}
                  to={redirectUrl ? `/login?redirect=${encodeURIComponent(redirectUrl)}` : '/login'}
                  sx={{
                    color: colors.accentPrimary,
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Sign In
                </Link>
              </Typography>
            </Box>
          </Box>
        </FormCard>
      </Container>
    </PageWrapper>
  );
};

export default RegisterPage;
